import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const API = config.apiUrl;

const HistoryPage = ({ user, setCurrentAssessment }) => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHistory();
  }, [user]);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/assessments/user/${user.id}`);
      setAssessments(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Erreur lors du chargement de l\'historique.');
      setLoading(false);
    }
  };

  const handleViewResults = async (assessmentId) => {
    try {
      const response = await axios.get(`${API}/assessments/${assessmentId}`);
      setCurrentAssessment({
        id: response.data.id,
        user_id: response.data.user_id,
        scores: response.data.scores,
        created_at: response.data.created_at
      });
      navigate('/results');
    } catch (err) {
      console.error('Error loading assessment:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="history-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Historique des évaluations</h1>
              <p className="text-slate-600">
                {user.first_name} {user.last_name}
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
              data-testid="new-assessment-btn"
            >
              Nouvelle évaluation
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6">
            {error}
          </div>
        )}

        {/* Assessment List */}
        {assessments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-slate-600 text-lg">Aucune évaluation trouvée</p>
            <p className="text-slate-500 text-sm mt-2">Commencez votre première évaluation pour voir votre historique ici.</p>
            <button
              onClick={() => navigate('/questionnaire')}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Commencer une évaluation
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {assessments.map((assessment, index) => {
              const date = new Date(assessment.created_at).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });

              const avgScore = Math.round(
                Object.values(assessment.scores).reduce((sum, m) => sum + m.percentage, 0) / 14
              );

              return (
                <div 
                  key={assessment.id} 
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-6"
                  data-testid={`assessment-${assessment.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                          Évaluation #{assessments.length - index}
                        </span>
                        <span className="text-slate-600 text-sm">{date}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <div>
                          <div className="text-sm text-slate-500">Score moyen</div>
                          <div className="text-2xl font-bold text-slate-800">{avgScore}%</div>
                        </div>
                        <div className="h-12 w-px bg-slate-200"></div>
                        <div>
                          <div className="text-sm text-slate-500">Mécanismes évalués</div>
                          <div className="text-2xl font-bold text-slate-800">14</div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewResults(assessment.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition ml-6"
                      data-testid={`view-results-btn-${assessment.id}`}
                    >
                      Voir les résultats
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
